import { OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs/';

export abstract class RxComponent implements OnInit, AfterViewInit, OnDestroy {

    private lifecycle: BehaviorSubject<ComponentEvent>;

    constructor() {
        this.lifecycle = new BehaviorSubject(ComponentEvent.Create);
    }

    public getLifecycle(): Observable<ComponentEvent> {
        return this.lifecycle.asObservable();
    }

    ngOnInit() {
        this.lifecycle.next(ComponentEvent.Init);
    }

    ngAfterViewInit() {
        this.lifecycle.next(ComponentEvent.AfterViewInit);
    }

    ngAfterContentInit() {
        this.lifecycle.next(ComponentEvent.AfterContentInit);
    }

    ngOnDestroy() {
        this.lifecycle.next(ComponentEvent.Destroy);
    }

    afterViewInit(): Observable<ComponentEvent> {
        return this.lifecycle.filter(e => e == ComponentEvent.AfterViewInit);
    }
}

export enum ComponentEvent {
    Create, Init, AfterViewInit, AfterContentInit, Destroy
}

declare module 'rxjs/Observable' {

    interface Observable<T> {

        /**
         * By binding to the lifecycle of the component, you guarantee that the observable will
         * properly complete (unsubscribe) after the component's ngOnDestroy() hook is called.
         *
         * **Note**: The component must extend the base RxComponent class. If you implement ngOnDestroy() yourself,
         * don't forget to call super.ngOnDestroy()!
         * @param {RxComponent} component - the component whose lifecycle you want to bind the observable to.
         */
        bindToLifecycle(component: RxComponent): Observable<T>;

        /**
         * Catches if an error is emitted by the source observable and calls onComplete() instead.
         */
        neverError(action?: (err) => void): Observable<T>;

        /**
         * @deprecated - Use takeWhen(...)
         * Waits for the given observable to emit an item.
         */
        waitUntil(until: Observable<T>): Observable<T>;

        /**
         * Waits for the given observable to emit an item.
         */
        takeWhen(until: Observable<any> | ((arg?) => Observable<any>)): Observable<T>;

        /**
         * Caches the latest N emissions from the source observable. Default: 1.
         */
        cacheResult(bufferSize?: number): Observable<T>;

        /**
         * Executes the action for every item or error emitted by the source observable.
         */
        always(action: () => void): Observable<T>;

        /**
         * Maps each value emitted with an index.
         */
        mapWithIndex<T>(): Observable<IndexWrapper<T>>;

        /**
         * Catches if an error is emitted by the source observable and
         * emits the provided value instead.
         */
        catchAndEmit<T>(value: T): Observable<T>;

        /**
         * Retrieves the specified nested value and emits it.
         */
        extract<T>(value: T): Observable<any>;

        /**
         * Works like Observable#startWith(value: T), but only emits
         * if the value is not null or undefined.
         */
        startWithSafe<T>(value: T): Observable<T>;
    }
}

export class IndexWrapper<T> {

    private readonly _index: number;
    private readonly _value: T;

    constructor(index: number, value: T) {
        this._index = index;
        this._value = value;
    }

    get index(): number {
        return this._index;
    }

    get value(): T {
        return this._value;
    }
}

Observable.prototype.bindToLifecycle = function (component: RxComponent): Observable<any> {
    let source = this;

    return source.takeUntil(
        component.getLifecycle().filter(e => e === ComponentEvent.Destroy)
    );
};

Observable.prototype.neverError = function (action: (err) => void): Observable<any> {
    let source = this;

    return source.catch(e => {
        if (action) {
            action(e);
        }

        return Observable.empty();
    });
};

Observable.prototype.waitUntil = function(until: Observable<any>): Observable<any> {
    let source = this;

    return until.take(1).flatMap(_ => source);
};

Observable.prototype.always = function (action: () => void): Observable<any> {
    let source = this;

    return source.do(
        item => action(),
        err => action(),
    );
};

Observable.prototype.cacheResult = function (bufferSize: number = 1): Observable<any> {
    let source = this;

    return source.publishReplay(bufferSize).refCount();
};

Observable.prototype.mapWithIndex = function<T>(): Observable<IndexWrapper<T>> {
    let source = this;

    let index = 0;
    return source.map(x => new IndexWrapper(index++, x));
};

Observable.prototype.catchAndEmit = function<T>(value: T): Observable<T> {
    let source = this;

    return source.catch(_ => Observable.of(value));
};

Observable.prototype.takeWhen = function<T>(until: Observable<any> | ((arg?) => Observable<any>)): Observable<T> {
    return this.switchMap(val => (until instanceof Observable ? until : until(val))
        .take(1)
        .map(_ => val)
    );
};

Observable.prototype.extract = function<T>(value: T): Observable<any> {
    let source = this;

    return this.pluck(value);
};

Observable.prototype.startWithSafe = function<T>(value: T): Observable<T> {
    let source = this;

    return (value !== null && value !== undefined) ? source.startWith(value) : source;
}

export class RxExtensions {

    static fromMutations(domNode: Node, config: MutationObserverInit): Observable<MutationRecord[]> {
        return Observable.create(observer => {
            let mutationsObserver = new MutationObserver(mutations => observer.next(mutations));
            mutationsObserver.observe(domNode, config);
            return () => mutationsObserver.disconnect();
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/core/components/common/rx.component.ts